import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, InputAdornment, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import HistoryIcon from "@mui/icons-material/History";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { fieldSx } from "../../egresos/components/formStyles";
import PapeletaDialog from "../components/PapeletaDialog";
import PagosDialog from "../components/PagosDialog";
import { dinero, listarEmpleadosPlanilla } from "../services/planillasService";

// Planillas de sueldos: empleados con contrato vigente, su total ganado y líquido estimado; desde aquí se emite la papeleta
// de pago (que crea el cheque en Egresos) y se consultan los pagos anteriores.
export default function PlanillasPage() {
  const [datos, setDatos] = useState({ items: [], empresas: [], departamentos: [] });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState(null);
  const [buscar, setBuscar] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [departamento, setDepartamento] = useState("");
  const [papeleta, setPapeleta] = useState(null);
  const [pagos, setPagos] = useState(null);
  const [orden, setOrden] = useState({ campo: null, dir: "asc" });

  const cargar = useCallback(async () => {
    setCargando(true);
    setError("");
    try {
      const { data } = await listarEmpleadosPlanilla({ buscar: buscar.trim() || undefined, empresa: empresa || undefined, departamento: departamento || undefined });
      setDatos(data);
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo cargar la lista de empleados.");
    } finally {
      setCargando(false);
    }
  }, [buscar, empresa, departamento]);

  useEffect(() => {
    const t = setTimeout(cargar, 250); // espera a que termine de escribir
    return () => clearTimeout(t);
  }, [cargar]);

  const total = useMemo(() => datos.items.reduce((a, x) => a + Number(x.liquido_estimado || 0), 0), [datos.items]);

  // Orden en el cliente: 1er clic asc, 2º desc, 3º vuelve al orden original.
  const ordenar = (campo) => setOrden((o) => (o.campo !== campo ? { campo, dir: "asc" } : o.dir === "asc" ? { campo, dir: "desc" } : { campo: null, dir: "asc" }));
  const filas = useMemo(() => {
    if (!orden.campo) return datos.items;
    const k = orden.campo;
    const signo = orden.dir === "asc" ? 1 : -1;
    const valor = (x) => (k === "anios" ? Number(x.anios || 0) * 1000 + Number(x.porcentaje_antiguedad || 0) : x[k]);
    return [...datos.items].sort((a, b) => {
      const va = valor(a);
      const vb = valor(b);
      if (va == null || va === "") return 1; // vacíos siempre al final
      if (vb == null || vb === "") return -1;
      const na = Number(va);
      const nb = Number(vb);
      const cmp = Number.isFinite(na) && Number.isFinite(nb) && typeof va !== "string"
        ? na - nb
        : String(va).localeCompare(String(vb), "es", { numeric: true, sensitivity: "base" });
      return cmp * signo;
    });
  }, [datos.items, orden]);
  const encabezado = (campo, texto, align) => (
    <TableCell sx={cabecera} align={align} sortDirection={orden.campo === campo ? orden.dir : false}>
      <TableSortLabel
        active={orden.campo === campo}
        direction={orden.campo === campo ? orden.dir : "asc"}
        onClick={() => ordenar(campo)}
        sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
      >
        {texto}
      </TableSortLabel>
    </TableCell>
  );

  const cabecera = { fontWeight: 800, fontSize: 11, whiteSpace: "nowrap", bgcolor: "primary.main", color: "primary.contrastText" };

  return (
    <DashboardLayout>
      <PageHeader title="Planillas" showBack={false} />
      <Box sx={{ p: { xs: 0.5, md: 1.25 } }}>
        <Alert severity="info" sx={{ py: 0, mb: 1, fontSize: 12 }}>
          Empleados con contrato vigente. El <strong>líquido estimado</strong> usa el sueldo básico, el bono de antigüedad, los descuentos de ley y el bono de incremento del contrato. Al emitir la papeleta se registra el cheque en Egresos y el pago.
        </Alert>
        {aviso && <Alert severity="success" onClose={() => setAviso(null)} sx={{ py: 0, mb: 1 }}>{aviso}</Alert>}
        {error && <Alert severity="error" sx={{ py: 0, mb: 1 }}>{error}</Alert>}

        <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap", alignItems: "center" }}>
          <TextField size="small" placeholder="Buscar por nombre o cargo" value={buscar} onChange={(e) => setBuscar(e.target.value)} sx={{ ...fieldSx, width: 260 }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }} />
          {datos.empresas.length > 1 && (
            <TextField select size="small" label="Empresa" value={empresa} onChange={(e) => setEmpresa(e.target.value)} sx={{ ...fieldSx, minWidth: 160 }}>
              <MenuItem value="">Todas</MenuItem>
              {datos.empresas.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
            </TextField>
          )}
          <TextField select size="small" label="Departamento" value={departamento} onChange={(e) => setDepartamento(e.target.value)} sx={{ ...fieldSx, minWidth: 170 }}>
            <MenuItem value="">Todos</MenuItem>
            {datos.departamentos.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
          </TextField>
          {cargando && <CircularProgress size={18} />}
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{datos.items.length} empleados · líquido estimado total Bs. {dinero(total)}</Typography>
        </Box>

        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <TableContainer>
            <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.35, px: 1, fontSize: 12 }, "& tbody tr:nth-of-type(even)": { bgcolor: "action.hover" } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={cabecera}>#</TableCell>
                  {encabezado("nombre_completo", "Empleado")}
                  {encabezado("departamento", "Departamento")}
                  {encabezado("anios", "Antigüedad", "right")}
                  {encabezado("total_ganado", "Total ganado", "right")}
                  {encabezado("liquido_estimado", "Líquido estimado", "right")}
                  {encabezado("ultimo_pago", "Último pago")}
                  <TableCell sx={cabecera} align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!cargando && datos.items.length === 0 && (
                  <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">No hay empleados con contrato vigente que coincidan.</Typography></TableCell></TableRow>
                )}
                {filas.map((x, i) => (
                  <TableRow key={x.id} hover>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{x.nombre_completo}</Typography>
                      <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{x.cargo}{Number(x.tipo) === 2 ? " · eventual" : ""}</Typography>
                    </TableCell>
                    <TableCell>{x.departamento}</TableCell>
                    <TableCell align="right">{x.anios} {x.anios === 1 ? "año" : "años"}{x.porcentaje_antiguedad ? ` · ${x.porcentaje_antiguedad}%` : ""}</TableCell>
                    <TableCell align="right">{dinero(x.total_ganado)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{dinero(x.liquido_estimado)}</TableCell>
                    <TableCell>{x.ultimo_pago || "—"}{x.anticipos_pendientes > 0 ? <Typography component="span" sx={{ fontSize: 10.5, color: "warning.main", ml: 0.75 }}>anticipo pendiente</Typography> : null}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Button size="small" startIcon={<ReceiptLongIcon fontSize="small" />} sx={{ py: 0, fontSize: 11 }} onClick={() => setPapeleta(x)}>Emitir planilla</Button>
                      <Button size="small" startIcon={<HistoryIcon fontSize="small" />} sx={{ py: 0, fontSize: 11 }} onClick={() => setPagos(x)}>Ver pagos</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {papeleta && <PapeletaDialog empleado={papeleta} onClose={() => setPapeleta(null)} onEmitida={() => { setAviso(`Planilla de ${papeleta.nombre_completo} emitida.`); cargar(); }} />}
      {pagos && <PagosDialog empleado={pagos} onClose={() => setPagos(null)} />}
    </DashboardLayout>
  );
}
